import { CheckIcon, X as XIcon } from 'lucide-react';
import type { FormEvent } from 'react';
import { EditCellOverlay } from './EditCellOverlay';

export interface EditCellFormProps {
  defaultValue: string | number;
  editableType?: 'text' | 'number' | 'select';
  editableOptions?: { value: string; label: string }[];
  min?: number;
  max?: number;
  step?: number | 'any';
  isSubmitting?: boolean;
  // Receives the submitted field value. The form owns its input's name attribute
  // and reads the value internally, so callers never reach into the form's DOM.
  // `refuse` shows its message on the field until the field is edited.
  onSubmit: (value: string, refuse: (message: string) => void) => void;
  onCancel: () => void;
  popupPosition?: 'above' | 'below';
  testIds?: {
    input?: string;
    submit?: string;
    cancel?: string;
  };
}

function fieldOf(form: HTMLFormElement) {
  const field = form.elements.namedItem('editTableInput');
  return field instanceof HTMLInputElement || field instanceof HTMLSelectElement
    ? field
    : null;
}

const inputClassName =
  'flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-prim focus:border-transparent';

export function EditCellForm({
  defaultValue,
  editableType,
  editableOptions,
  min,
  max,
  step = 'any',
  isSubmitting = false,
  onSubmit,
  onCancel,
  popupPosition = 'above',
  testIds,
}: EditCellFormProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const field = fieldOf(event.currentTarget);
    if (field) {
      onSubmit(field.value, (message) => {
        field.setCustomValidity(message);
        field.reportValidity();
      });
    }
  };

  return (
    <EditCellOverlay position={popupPosition}>
      <form
        className="flex items-center gap-2"
        onSubmit={handleSubmit}
        onInput={(e) => {
          const field = fieldOf(e.currentTarget);
          if (field) {
            field.setCustomValidity('');
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
      >
        {editableType === 'select' ? (
          <select
            data-testid={testIds?.input}
            name="editTableInput"
            autoFocus
            defaultValue={defaultValue as string}
            className={inputClassName}
          >
            {editableOptions?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : (
          <input
            data-testid={testIds?.input}
            name="editTableInput"
            autoFocus
            type={editableType === 'number' ? 'number' : 'text'}
            step={editableType === 'number' ? step : undefined}
            min={editableType === 'number' ? min : undefined}
            max={editableType === 'number' ? max : undefined}
            defaultValue={defaultValue}
            className={inputClassName}
          />
        )}
        <button
          className="p-2 text-white bg-prim hover:bg-prim-hover rounded transition-colors disabled:opacity-50 shrink-0"
          disabled={isSubmitting}
          type="submit"
          title="Save"
          data-testid={testIds?.submit}
        >
          <CheckIcon size={16} />
        </button>
        <button
          className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 shrink-0"
          type="button"
          title="Cancel"
          disabled={isSubmitting}
          onClick={onCancel}
          data-testid={testIds?.cancel}
        >
          <XIcon size={16} />
        </button>
      </form>
    </EditCellOverlay>
  );
}
